import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  type ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';

const poolData: ICognitoUserPoolData = {
  UserPoolId: import.meta.env.VITE_USER_POOL_ID,
  ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

export interface AuthTokens {
  idToken: string;
  accessToken: string;
}

export interface NewPasswordRequiredResult {
  status: 'NEW_PASSWORD_REQUIRED';
  completeNewPassword: (newPassword: string) => Promise<AuthTokens>;
}

export interface SuccessResult {
  status: 'SUCCESS';
  tokens: AuthTokens;
}

/** MyHours-ADR-0006/D-24: nach einem Admin-initiierten Reset (AdminResetUserPassword) -- kein Code
 * wurde bereits verschickt, der Aufrufer muss noch forgotPassword() auslösen. */
export interface PasswordResetRequiredResult {
  status: 'PASSWORD_RESET_REQUIRED';
}

export type LoginResult = SuccessResult | NewPasswordRequiredResult | PasswordResetRequiredResult;

function tokensFromSession(session: CognitoUserSession): AuthTokens {
  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
  };
}

/**
 * MyHours-ADR-0043: Die E-Mail wird vor JEDEM Cognito-Aufruf normalisiert — getrimmt und
 * kleingeschrieben.
 *
 * Der Pool wurde ohne `UsernameConfiguration` angelegt, und AWS' Voreinstellung ist dann
 * **case-sensitive**. Gemessen am 2026-09-14: `Kevinpc_weiss@web.de` wird gefunden,
 * `kevinpc_weiss@web.de` nicht. Ohne Normalisierung entscheidet also die Groß-/Kleinschreibung
 * der Eingabe über das Gelingen der Anmeldung — und Cognito antwortet dann mit „Incorrect
 * username or password", was auf das Passwort zeigt statt auf die Adresse. Der Betroffene setzt
 * sein Passwort zurück, scheitert erneut und kommt nie auf die Schreibweise.
 *
 * `UsernameConfiguration` ist nach dem Anlegen des Pools unveränderlich; echte
 * Case-Insensitivität gäbe es nur mit einem neuen Pool und damit neuen Passwörtern für alle.
 * Diese Normalisierung erreicht praktisch dasselbe.
 *
 * **Setzt voraus, dass alle gespeicherten Adressen kleingeschrieben sind** — sonst wäre sie ein
 * Eigentor: Wer heute mit Großbuchstaben gespeichert ist, käme danach gar nicht mehr hinein.
 * Die vier Prod-Bestandsfälle wurden vor dem Ausrollen umgestellt (siehe MyHours-ADR-0043).
 */
function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** SRP login against Cognito directly -- no Hosted UI. */
export function login(email: string, password: string): Promise<LoginResult> {
  const username = normalizeEmail(email);
  const user = new CognitoUser({ Username: username, Pool: userPool });
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ status: 'SUCCESS', tokens: tokensFromSession(session) }),
      onFailure: (err) => {
        if (
          err &&
          typeof err === 'object' &&
          'code' in err &&
          err.code === 'PasswordResetRequiredException'
        ) {
          resolve({ status: 'PASSWORD_RESET_REQUIRED' });
          return;
        }
        reject(err);
      },
      newPasswordRequired: () => {
        resolve({
          status: 'NEW_PASSWORD_REQUIRED',
          completeNewPassword: (newPassword: string) =>
            new Promise<AuthTokens>((res, rej) => {
              user.completeNewPasswordChallenge(
                newPassword,
                {},
                {
                  onSuccess: (session) => res(tokensFromSession(session)),
                  onFailure: (err) => rej(err),
                },
              );
            }),
        });
      },
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: normalizeEmail(email), Pool: userPool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const user = new CognitoUser({ Username: normalizeEmail(email), Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Ends the Cognito session in this browser only (no GlobalSignOut, no
 * RevokeToken). Sweeps every localStorage key under this client's storage
 * prefix -- `CognitoUser.signOut()` alone only clears the *current* user's
 * cached tokens, not a previous, never-logged-out user's on a shared device.
 */
export function logout(): void {
  userPool.getCurrentUser()?.signOut();
  const prefix = `CognitoIdentityServiceProvider.${poolData.ClientId}.`;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) localStorage.removeItem(key);
  }
}

/** Returns a valid (refreshed if needed) ID token for the currently signed-in user, if any. */
export function getCurrentIdToken(): Promise<string | undefined> {
  const user = userPool.getCurrentUser();
  if (!user) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(undefined);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

/**
 * MyHours-ADR-0016/D-85: erzwingt ein neues ID-Token nach einer serverseitigen Attribut-Änderung (z.B.
 * PUT /admin/users/me) -- ein gültiges, gecachtes Token wird von `getCurrentIdToken()`/`getSession`
 * sonst unverändert zurückgegeben, ohne die neuen Attribute (given_name/family_name) zu enthalten.
 */
export function refreshOwnSession(): Promise<void> {
  const user = userPool.getCurrentUser();
  if (!user) return Promise.reject(new Error('Nicht angemeldet'));
  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        reject(err ?? new Error('Sitzung ungültig'));
        return;
      }
      user.refreshSession(session.getRefreshToken(), (refreshErr) => {
        if (refreshErr) reject(refreshErr);
        else resolve();
      });
    });
  });
}

export function getCurrentUserEmail(): string | undefined {
  return userPool.getCurrentUser()?.getUsername();
}

/**
 * `amazon-cognito-identity-js` throws raw AWS error objects (English `.message`, e.g.
 * "Incorrect username or password.") straight out of the SDK -- unlike every other error in this
 * app, which comes from our own backend and is already German. Maps the known Cognito exception
 * codes actually seen on the login/reset-password screens to clear German text.
 */
export function translateAuthError(err: unknown, fallback: string): string {
  const code =
    err && typeof err === 'object' && 'code' in err ? (err as { code?: string }).code : undefined;
  switch (code) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'E-Mail oder Passwort ist falsch.';
    case 'CodeMismatchException':
      return 'Der eingegebene Code ist ungültig. Bitte prüfen Sie die E-Mail und versuchen Sie es erneut.';
    case 'ExpiredCodeException':
      return 'Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.';
    case 'LimitExceededException':
      return 'Zu viele Versuche. Bitte warten Sie einen Moment, bevor Sie es erneut versuchen.';
    case 'InvalidPasswordException':
      return 'Das Passwort erfüllt nicht die Anforderungen (mind. 10 Zeichen, Groß- und Kleinbuchstaben sowie Zahlen).';
    default:
      return fallback;
  }
}
